
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { format, parseISO } from "date-fns"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useTrainer } from "@/contexts/trainer-context"
import { mockUsers } from "@/lib/mock-data"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface AppointmentDetailsProps {
  appointmentId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AppointmentDetails({ appointmentId, open, onOpenChange }: AppointmentDetailsProps) {
  const { t } = useTranslation()
  const { appointments, updateAppointment, deleteAppointment } = useTrainer()

  const appointment = appointments.find((a) => a.id === appointmentId)

  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const [startTime, setStartTime] = useState(appointment?.startTime || "")
  const [endTime, setEndTime] = useState(appointment?.endTime || "")
  const [type, setType] = useState(appointment?.type || "")
  const [notes, setNotes] = useState(appointment?.notes || "")
  const [status, setStatus] = useState(appointment?.status || "scheduled")

  if (!appointment) {
    return null
  }

  const user = mockUsers.find((u) => u.id === appointment.userId)

  const handleSave = () => {
    updateAppointment(appointmentId, {
      startTime,
      endTime,
      type,
      notes,
      status,
    })
    setIsEditing(false)
  }

  const handleDelete = () => {
    deleteAppointment(appointmentId)
    setIsDeleteDialogOpen(false)
    onOpenChange(false)
  }

  const appointmentTypes = [
    "Strength Training",
    "Cardio",
    "Flexibility",
    "HIIT",
    "Yoga",
    "Nutrition Consultation",
    "Assessment",
  ]

  const statusOptions = [
    { value: "scheduled", label: t("trainerView.appointmentDetails.statusScheduled") },
    { value: "completed", label: t("trainerView.appointmentDetails.statusCompleted") },
    { value: "cancelled", label: t("trainerView.appointmentDetails.statusCancelled") },
  ]

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? t("trainerView.appointmentDetails.editTitle")
                : t("trainerView.appointmentDetails.viewTitle")}
            </DialogTitle>
            <DialogDescription>{format(parseISO(appointment.date), "EEEE, MMMM d, yyyy")}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right font-medium">{t("trainerView.appointmentDetails.client")}</Label>
              <div className="col-span-3">{user?.name || "Unknown User"}</div>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="start-time" className="text-right font-medium">
                {t("trainerView.appointmentDetails.time")}
              </Label>
              {isEditing ? (
                <div className="col-span-3 flex gap-2">
                  <Input id="start-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                  <span className="flex items-center">-</span>
                  <Input id="end-time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </div>
              ) : (
                <div className="col-span-3">
                  {appointment.startTime} - {appointment.endTime}
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right font-medium">
                {t("trainerView.appointmentDetails.type")}
              </Label>
              {isEditing ? (
                <Select value={type} onValueChange={setType} required>
                  <SelectTrigger id="type" className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {appointmentTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="col-span-3">{appointment.type}</div>
              )}
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right font-medium">
                {t("trainerView.appointmentDetails.status")}
              </Label>
              {isEditing ? (
                <Select value={status} onValueChange={(value) => setStatus(value as "cancelled" | "completed" | "scheduled")} required>
                  <SelectTrigger id="status" className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="col-span-3">{statusOptions.find((o) => o.value === appointment.status)?.label}</div>
              )}
            </div>

            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="notes" className="text-right font-medium">
                {t("trainerView.appointmentDetails.notes")}
              </Label>
              {isEditing ? (
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="col-span-3"
                  rows={3}
                />
              ) : (
                <div className="col-span-3 whitespace-pre-wrap">
                  {appointment.notes || t("trainerView.appointmentDetails.noNotes")}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {!isEditing && (
                <Button variant="destructive" onClick={() => setIsDeleteDialogOpen(true)}>
                  {t("trainerView.appointmentDetails.delete")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    {t("trainerView.appointmentDetails.cancel")}
                  </Button>
                  <Button onClick={handleSave}>{t("trainerView.appointmentDetails.save")}</Button>
                </>
              ) : (
                <Button onClick={() => setIsEditing(true)}>{t("trainerView.appointmentDetails.edit")}</Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("trainerView.appointmentDetails.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("trainerView.appointmentDetails.deleteConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("trainerView.appointmentDetails.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {t("trainerView.appointmentDetails.confirmDelete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
